import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import ETFPriceDisplay from '@/components/etf-price-display/ETFPriceDisplay';
import { formatCurrency, formatNumber } from '@/lib/currency.utils';
import {ETFHolding} from "@/integrations/supabase/etf.models.ts";

interface ETFHoldingsTableProps {
  holdings: ETFHolding[];
  prices: Record<string, { price: number; change: number; changePercent: number; currency: string } | null>;
  onEdit: (holding: ETFHolding) => void;
  onDelete: (holding: ETFHolding) => void;
  onRowClick: (holding: ETFHolding) => void;
}

const CATEGORY_BADGE_CLASSES: Record<ETFCategory, string> = {
  equity: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  fixed_income: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  commodity: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  real_estate: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  multi_asset: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

const CATEGORY_LABELS: Record<ETFCategory, string> = {
  equity: 'Equity',
  fixed_income: 'Fixed Income',
  commodity: 'Commodity',
  real_estate: 'Real Estate',
  multi_asset: 'Multi Asset',
};

function computeReturn(
  priceData: { price: number } | null | undefined,
  holding: ETFHolding
): { totalReturn: number; totalReturnPct: number } | null {
  if (!priceData) return null;
  const totalReturn = (priceData.price - holding.average_cost_per_unit) * holding.units_held;
  const totalReturnPct =
    holding.average_cost_per_unit > 0
      ? ((priceData.price - holding.average_cost_per_unit) / holding.average_cost_per_unit) * 100
      : 0;
  return { totalReturn, totalReturnPct };
}

// Lazy import for ETFPriceTrendChart — it may not exist yet (task 12).
// We use a try/catch dynamic import pattern via a wrapper component.
let ETFPriceTrendChart: React.ComponentType<{ tickerSymbol: string; period?: string; compact?: boolean }> | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  ETFPriceTrendChart = require('@/components/etf-price-trend-chart/ETFPriceTrendChart').ETFPriceTrendChart
    ?? require('@/components/etf-price-trend-chart/ETFPriceTrendChart').default
    ?? null;
} catch {
  ETFPriceTrendChart = null;
}

function SparklineCell({ ticker }: { ticker: string }) {
  if (!ETFPriceTrendChart) return null;
  return (
    <div className="w-20 h-8" data-testid={`sparkline-${ticker}`}>
      <ETFPriceTrendChart tickerSymbol={ticker} period="1W" compact />
    </div>
  );
}

export function ETFHoldingsTable({
  holdings,
  prices,
  onEdit,
  onDelete,
  onRowClick,
}: ETFHoldingsTableProps) {
  if (holdings.length === 0) {
    return (
      <div
        className="flex items-center justify-center py-12 text-muted-foreground text-sm"
        data-testid="empty-state"
      >
        No ETF holdings yet. Add your first ETF to get started.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto w-full">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Asset</TableHead>
            <TableHead className="text-right">Units</TableHead>
            <TableHead className="text-right">Avg Cost</TableHead>
            <TableHead>Market Price</TableHead>
            <TableHead className="text-right">Total Return</TableHead>
            <TableHead>Trend</TableHead>
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {holdings.map((holding) => {
            const priceData = prices[holding.ticker_symbol];
            const returnData = computeReturn(priceData, holding);
            const isPositive = returnData ? returnData.totalReturn >= 0 : null;

            return (
              <TableRow
                key={holding.id}
                className="cursor-pointer"
                onClick={() => onRowClick(holding)}
                data-testid={`holding-row-${holding.id}`}
              >
                {/* Asset column */}
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-sm">{holding.ticker_symbol}</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[160px]">
                      {holding.etf_name}
                    </span>
                    <Badge
                      className={`text-xs w-fit px-1.5 py-0 border-0 ${CATEGORY_BADGE_CLASSES[holding.category]}`}
                      data-testid={`category-badge-${holding.id}`}
                    >
                      {CATEGORY_LABELS[holding.category]}
                    </Badge>
                  </div>
                </TableCell>

                {/* Units */}
                <TableCell className="text-right font-mono text-sm">
                  {formatNumber(holding.units_held, 4)}
                </TableCell>

                {/* Avg Cost */}
                <TableCell className="text-right font-mono text-sm">
                  {formatCurrency(holding.average_cost_per_unit, holding.currency)}
                </TableCell>

                {/* Market Price */}
                <TableCell>
                  <ETFPriceDisplay symbol={holding.ticker_symbol} />
                </TableCell>

                {/* Total Return */}
                <TableCell className="text-right">
                  {returnData === null ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <div
                      className={`flex flex-col items-end text-sm font-mono ${
                        isPositive ? 'text-emerald-600' : 'text-red-600'
                      }`}
                      data-testid={`total-return-${holding.id}`}
                    >
                      <span>
                        {isPositive ? '+' : ''}
                        {formatCurrency(returnData.totalReturn, holding.currency)}
                      </span>
                      <span className="text-xs">
                        {isPositive ? '+' : ''}
                        {returnData.totalReturnPct.toFixed(2)}%
                      </span>
                    </div>
                  )}
                </TableCell>

                {/* Sparkline */}
                <TableCell>
                  <SparklineCell ticker={holding.ticker_symbol} />
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(holding);
                      }}
                      aria-label={`Edit ${holding.ticker_symbol}`}
                      data-testid={`edit-btn-${holding.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(holding);
                      }}
                      aria-label={`Delete ${holding.ticker_symbol}`}
                      data-testid={`delete-btn-${holding.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
